import React from 'react';
import { Text, Button, Flex, TagBadge } from '@chatsdet/ui';
import type { ApiSequenceConfig, ApiCatalogListItem } from '@chatsdet/types';
import { PencilSimple, ArrowsDownUp } from '@phosphor-icons/react';

type ApiSequencePreviewProps = {
  config: ApiSequenceConfig;
  apiRequests: ApiCatalogListItem[];
  onEdit: () => void;
};

/**
 * Compact preview of an API sequence configuration.
 * Shows step count and a summary of the first few steps with an Edit button.
 */
export function ApiSequencePreview({ config, apiRequests, onEdit }: ApiSequencePreviewProps) {
  const steps = config.steps || [];
  const maxPreviewSteps = 3;

  const getStepLabel = (step: { apiRequestId: string; method?: string }) => {
    const request = apiRequests.find((r) => r.id === step.apiRequestId);
    if (request) {
      const method = step.method || request.method;
      // Domain is the discriminator between endpoints that otherwise share
      // the same normalized pattern across different properties.
      const domain = request.domain ? ` · ${request.domain}` : '';
      return `${method} ${request.pattern}${domain}`;
    }
    if (step.method) {
      return step.method;
    }
    return 'Unconfigured';
  };

  return (
    <div className="space-y-4">
      {/* Summary header */}
      <Flex align="center" justify="between">
        <Flex align="center" gap="sm">
          <ArrowsDownUp size={16} className="text-muted-foreground" />
          <Text size="sm" weight="medium">
            {steps.length} API call{steps.length !== 1 ? 's' : ''}
          </Text>
        </Flex>
        <Button variant="outline" size="sm" onPress={onEdit} iconLeft={<PencilSimple size={14} />}>
          Edit Sequence
        </Button>
      </Flex>

      {/* Step previews */}
      {steps.length > 0 ? (
        <div className="space-y-2">
          {steps.slice(0, maxPreviewSteps).map((step, index) => (
            <div
              key={step.stepId || index}
              className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted/30 border border-border/50"
            >
              <TagBadge name={index + 1} color="gray" hideDot size="sm" className="shrink-0" />
              <Text size="sm" className="font-mono text-muted-foreground truncate">
                {getStepLabel(step)}
              </Text>
            </div>
          ))}
          {steps.length > maxPreviewSteps && (
            <Text size="xs" className="text-muted-foreground pl-3">
              +{steps.length - maxPreviewSteps} more step{steps.length - maxPreviewSteps !== 1 ? 's' : ''}
            </Text>
          )}
        </div>
      ) : (
        <div className="border-2 border-dashed rounded-lg p-4 text-center">
          <Text size="sm" className="text-muted-foreground">
            No API calls configured yet
          </Text>
        </div>
      )}

      {/* Description if present */}
      {config.description && (
        <Text size="xs" className="text-muted-foreground">
          {config.description}
        </Text>
      )}

      {/* Stop on failure indicator */}
      {config.stopOnFailure !== false && steps.length > 1 && (
        <Text size="xs" className="text-muted-foreground">
          Stops on first failure
        </Text>
      )}
    </div>
  );
}
